<template>
  <div id="tour-teachers-page" class="flex flex-col h-[calc(100vh-2rem)] space-y-2 p-2.5 w-full">
    <div class="flex-1 bg-white rounded-lg shadow-sm flex flex-col overflow-hidden relative">
      <div class="p-3 overflow-y-auto flex-1">
        <TeachersList>
          <template #actions>
            <span id="tour-teachers-invite-btn" class="inline-flex">
              <Button
                label="Invite Staff"
                icon="pi pi-user-plus"
                size="small"
                @click="showInviteDialog = true"
                data-testid="btn-invite-staff-dialog"
              />
            </span>
            <Button
              label="Invited Staff"
              icon="pi pi-list"
              severity="secondary"
              size="small"
              outlined
              @click="router.push({ name: 'admin-invited-staff' })"
            />
          </template>
        </TeachersList>
      </div>
    </div>

    <!-- Invite Dialog -->
    <Dialog
      v-model:visible="showInviteDialog"
      modal
      :style="{ width: inviteMode === 'bulk' ? '560px' : '440px' }"
      :pt="{ root: { class: 'rounded-xl overflow-hidden' } }"
      @hide="onInviteDialogHide"
    >
      <template #header>
        <div class="flex items-center gap-2">
          <div class="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50">
            <i class="pi pi-user-plus text-emerald-600 text-sm"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-sm">Invite Staff</h3>
            <p class="text-xs text-gray-500">Send an email invitation to join</p>
          </div>
        </div>
      </template>

      <div class="space-y-3">
        <div class="flex rounded-lg bg-gray-100 p-0.5 text-xs font-medium">
          <button
            class="flex-1 rounded-md px-2 py-1 transition-colors"
            :class="inviteMode === 'single' ? 'bg-white shadow text-gray-800' : 'text-gray-500'"
            @click="inviteMode = 'single'"
            data-testid="btn-invite-mode-single"
          >
            Single
          </button>
          <button
            class="flex-1 rounded-md px-2 py-1 transition-colors"
            :class="inviteMode === 'bulk' ? 'bg-white shadow text-gray-800' : 'text-gray-500'"
            @click="inviteMode = 'bulk'"
            data-testid="btn-invite-mode-bulk"
          >
            Bulk
          </button>
        </div>

        <BulkInviteStaff v-if="inviteMode === 'bulk'" />

        <div v-if="inviteMode === 'single'">
          <label class="block text-xs font-medium text-gray-600 mb-1">Email address</label>
          <InputText
            v-model="inviteEmail"
            type="email"
            placeholder="e.g. john@example.com"
            class="w-full"
            data-testid="input-invite-staff-email"
            @keyup.enter="sendInvitation"
          />
        </div>
        <div v-if="inviteMode === 'single'">
          <label class="block text-xs font-medium text-gray-600 mb-1">Role (optional)</label>
          <Select
            v-model="selectedRoleId"
            :options="roleOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Role (optional)"
            showClear
            :loading="loadingRoles"
            class="w-full"
            data-testid="invite-role-select"
          />
        </div>
        <div v-if="inviteMode === 'single' && inviteError" class="text-red-600 text-xs bg-red-50 rounded-lg px-2.5 py-1.5" data-testid="invite-error">
          <div class="flex items-start gap-1.5">
            <i class="pi pi-exclamation-circle mt-0.5"></i>
            <span>{{ inviteError.message }}</span>
          </div>
          <!-- The address already has an invitation waiting; the useful next
               step is refreshing that one, not sending a second. -->
          <Button
            v-if="canResendInvitation"
            label="Resend invitation"
            icon="pi pi-refresh"
            severity="danger"
            size="small"
            text
            class="mt-1 !px-0"
            :loading="resending"
            @click="resendInvitation"
            data-testid="btn-resend-invitation"
          />
        </div>
        <div v-if="inviteMode === 'single' && inviteSuccess" class="flex items-center gap-1.5 text-emerald-600 text-xs bg-emerald-50 rounded-lg px-2.5 py-1.5">
          <i class="pi pi-check-circle"></i>
          <span>{{ inviteSuccess }}</span>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="closeInviteDialog" data-testid="btn-invite-staff-cancel" />
          <Button
            v-if="inviteMode === 'single'"
            label="Send Invitation"
            icon="pi pi-send"
            size="small"
            :loading="inviteLoading"
            :disabled="!inviteEmail.trim()"
            @click="sendInvitation"
            data-testid="btn-send-staff-invitation"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import TeachersList from '@/components/DashBoard/Teacher/TeachersList.vue';
import { useInviteTeacher, InviteErrorCode } from '@/composables/useInviteTeacher';
import { useRoleOptions } from '@/composables/useRoleOptions';

const router = useRouter();

const showInviteDialog = ref(false);
const inviteMode = ref<'single' | 'bulk'>('single');
const inviteEmail = ref('');
const inviteSuccess = ref('');

// Optional role to pre-assign at invite time. Shares the role-options source
// (#264's useRoleOptions) with the bulk-invite and role-request flows; an
// empty selection keeps the email-only behaviour.
const selectedRoleId = ref<string | null>(null);
const { roleOptions, loadingRoles } = useRoleOptions();

const {
  inviteTeacher,
  resendInvitation: resendPendingInvitation,
  loading: inviteLoading,
  resending,
  error: inviteError,
  onDone,
} = useInviteTeacher();

// Only the duplicate-invite case has an existing invitation to refresh.
const canResendInvitation = computed(() =>
  inviteError.value?.code === InviteErrorCode.InvitationPending && !!inviteError.value.invitationId,
);

const sendInvitation = async() => {
  if (!inviteEmail.value.trim()) return;
  inviteSuccess.value = '';
  await inviteTeacher({
    email: inviteEmail.value,
    school_permission_group_id: selectedRoleId.value || null,
  });
};

const resendInvitation = async() => {
  const email = inviteEmail.value;
  if (await resendPendingInvitation()) {
    inviteSuccess.value = `Invitation resent to ${email}.`;
    inviteEmail.value = '';
    selectedRoleId.value = null;
  }
};

const closeInviteDialog = () => {
  showInviteDialog.value = false;
};

// Fires for every close path (Cancel, X, Esc, mask click) so the next open
// always starts clean — single mode, empty form, no stale error/success.
const onInviteDialogHide = () => {
  inviteMode.value = 'single';
  inviteEmail.value = '';
  inviteSuccess.value = '';
  selectedRoleId.value = null;
  inviteError.value = null;
};

onDone(() => {
  inviteSuccess.value = `Invitation sent to ${inviteEmail.value}.`;
  inviteEmail.value = '';
  selectedRoleId.value = null;
});
</script>
